import { Alert, Button, Comment, Form } from 'antd';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import styled from 'styled-components';
import { MessageType } from '@app-types/index';
import { yupResolver } from '@hookform/resolvers';
import {
  STUDENT_REQUEST_MESSAGES,
  student_request_messageSubscription,
  student_request_messageSubscriptionVariables,
} from '@app-types/graphql-types';
import { useSubscription } from '@apollo/client';
import { HSMSLoading } from '@components/shared/HSMSLoading';
import { studentRequestMessageOnSend } from '@functions/mutations/studentRequestMessageOnSend';
import { useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import dynamic from 'next/dynamic';
import { HSMSInput } from './HSMSInput';

const SimpleBar = dynamic(() => import('simplebar-react'));

const MessageSchema: Yup.AnyObjectSchema = Yup.object().shape({
  content: Yup.string().required('Please enter a message.'),
});

interface HSMSChatWindowProps {
  studentRequestId: number;
  userId?: string;
  studentId?: number;
}

const HSMSMessages = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-right: 15px;
  height: 20rem;

  body {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .message {
    border-radius: 4px;
    padding: 8px 15px;
    margin-top: 5px;
    margin-bottom: 5px;
    display: inline-block;
  }

  .author-name {
    display: block;
    font-size: 10px;
    margin-top: -5px;
    margin-bottom: 2px;
  }

  .yours {
    align-items: flex-start;
  }

  .yours .message {
    margin-right: 25%;
    background-color: #eee;
    position: relative;
  }

  .yours .message.last:before {
    content: '';
    position: absolute;
    z-index: 0;
    bottom: 0;
    left: -7px;
    height: 20px;
    width: 20px;
    background: #eee;
    border-bottom-right-radius: 15px;
  }
  .yours .message.last:after {
    content: '';
    position: absolute;
    z-index: 1;
    bottom: 0;
    left: -10px;
    width: 10px;
    height: 20px;
    background: white;
    border-bottom-right-radius: 10px;
  }

  .mine {
    align-items: flex-end;
    text-align: right;
  }

  .mine .message {
    color: white;
    background: linear-gradient(to bottom, #fcbc71 0%, #f7931c 100%);
    background-attachment: fixed;
    position: relative;
  }

  .mine .message.last:before {
    content: '';
    position: absolute;
    z-index: 0;
    bottom: 0;
    right: -8px;
    height: 20px;
    width: 20px;
    background: linear-gradient(to bottom, #ffc786 0%, #f58d17 100%);
    background-attachment: fixed;
    border-bottom-left-radius: 15px;
  }

  .mine .message.last:after {
    content: '';
    position: absolute;
    z-index: 1;
    bottom: 0;
    right: -10px;
    width: 10px;
    height: 20px;
    background: white;
    border-bottom-left-radius: 10px;
  }
`;

export const HSMSChatWindow = ({ studentId, userId, studentRequestId }: HSMSChatWindowProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { addToast } = useToasts();
  const { handleSubmit, control, errors, reset } = useForm<MessageType>({
    resolver: yupResolver(MessageSchema),
    defaultValues: {
      content: '',
    },
  });

  const onSubmit = handleSubmit((messageData) => {
    studentRequestMessageOnSend({
      message: messageData.content,
      reset,
      studentRequestId,
      studentId,
      userId,
      setIsLoading,
      addToast,
    }).then();
  });

  const { data: studentRequestMessageData, loading: studentRequestMessageLoading } = useSubscription<
    student_request_messageSubscription,
    student_request_messageSubscriptionVariables
  >(STUDENT_REQUEST_MESSAGES, {
    variables: {
      where: {
        studentRequestId: {
          _eq: studentRequestId,
        },
      },
    },
  });

  const messages = studentRequestMessageData?.student_request_message;

  if (studentRequestMessageLoading && !messages) {
    return <HSMSLoading />;
  }

  return (
    <>
      <SimpleBar style={{ maxHeight: 400 }} id="chat-messages">
        <HSMSMessages>
          {messages.length === 0 ? (
            <Alert message="No messages to show" type="info" showIcon />
          ) : (
            messages.map((message, index) => {
              const mine: boolean = message.studentId === studentId || message.userId === userId;
              const authorName = message.student?.firstName || message.user_public?.firstName;

              return (
                <div key={message.id} className={`${mine ? 'mine' : 'yours'} messages`}>
                  <div className={`message ${messages.length - 1 === index ? 'last' : ''}`}>{message.content}</div>
                  {!mine && authorName && <span className="author-name">{authorName}</span>}
                </div>
              );
            })
          )}
        </HSMSMessages>
      </SimpleBar>

      <Comment
        content={
          <form onSubmit={onSubmit}>
            <HSMSInput
              errors={errors}
              validationSchema={MessageSchema}
              control={control}
              name="content"
              textArea
              rows={3}
              disabled={isLoading}
            />
            <Form.Item>
              <Button htmlType="submit" type="primary" loading={isLoading}>
                Send Message
              </Button>
            </Form.Item>
          </form>
        }
      />
    </>
  );
};
